// SEO topic-cluster content: pillar = /resources, clusters = /resources/:slug
// Each article interlinks to services, cities and sibling articles for topical authority.

const GROVE = "https://customer-assets.emergentagent.com/job_heavy-lift-solutions-3/artifacts/fabzdy7b_379E0A4F-9557-483E-91F7-55A4134B3FAF-800x600.jpeg";
const MANITEX = "https://customer-assets.emergentagent.com/job_heavy-lift-solutions-3/artifacts/08j7l5sd_IMG_4360.jpeg";
const HVAC = "https://images.pexels.com/photos/19016904/pexels-photo-19016904.jpeg";
const STEEL = "https://images.unsplash.com/photo-1493476523860-a6de6ce1b0c3";
const SITE_HERO = "https://images.pexels.com/photos/9906887/pexels-photo-9906887.jpeg";

// Article body blocks: { type: "h2"|"p"|"ul"|"callout", text | items }
export const ARTICLES = {
  "crane-rental-cost-central-florida": {
    slug: "crane-rental-cost-central-florida",
    title: "How Much Does Crane Rental Cost in Central Florida?",
    metaTitle: "Crane Rental Cost in Central Florida — 2025 Pricing Guide",
    metaDescription:
      "What does it cost to rent a crane with an operator in Central Florida? A plain-English breakdown of day rates, mobilization, minimums and what drives crane rental pricing in the DeLand, Orlando and Daytona area.",
    keywords:
      "crane rental cost central florida, how much to rent a crane florida, crane rental price orlando, operated crane rental rates deland",
    category: "Pricing",
    readTime: "6 min read",
    image: MANITEX,
    excerpt:
      "Day rates, mobilization, minimums and the real factors that move a crane quote up or down in Central Florida.",
    updated: "2025",
    body: [
      { type: "p", text: "It is the first question every contractor and property owner asks: what will a crane cost? The honest answer is that operated crane rental is priced per job, not off a fixed menu — but the factors that drive the number are predictable. Here is how pricing actually works in the DeLand, Orlando and Daytona Beach market." },
      { type: "h2", text: "What's included in an operated crane rental" },
      { type: "p", text: "At Alliance Crane Service, every rental is quoted with the crane, a certified operator, fuel and standard rigging included. You are not sourcing an operator separately or carrying the crane's insurance — it is one line item, one point of contact." },
      { type: "h2", text: "The main factors that drive your quote" },
      { type: "ul", items: [
        "Crane size — our 40-ton Grove is priced below our 50-ton Manitex because it is a smaller, lighter machine.",
        "Duration — half-day vs full-day vs multi-day. Most residential jobs are half-day or full-day.",
        "Mobilization — the round-trip travel from our DeLand yard. Local jobs cost less; a Jacksonville or Melbourne run adds mob/demob.",
        "Access and setup — tight sites, outrigger matting, and long-reach configurations take more time.",
        "Scheduling — standard scheduled work is priced better than urgent same-day or after-hours emergency calls.",
      ] },
      { type: "callout", text: "Rule of thumb: for most single-day residential and light-commercial picks close to DeLand, you are looking at a predictable half-day or full-day rate plus mobilization. Call (386) 506-7771 for a real number on your job." },
      { type: "h2", text: "Ways to keep crane costs down" },
      { type: "ul", items: [
        "Book ahead — non-emergency scheduling always prices better than a rush.",
        "Batch nearby work — if you have multiple picks, doing them in one visit saves mobilization.",
        "Have the site ready — clear access, known ground conditions and staged materials reduce on-site time.",
        "Pick the right crane — an honest pre-lift conversation makes sure you are not paying for more crane than the job needs.",
      ] },
      { type: "h2", text: "Get an exact quote" },
      { type: "p", text: "Every project is different. The fastest way to a real number is a two-minute phone call or a quote request with your lift details — weight, height, location and date." },
    ],
    faqs: [
      { q: "Do you charge a minimum for crane rental?", a: "Yes — like most operated crane companies, we have a minimum (typically a half-day) because mobilizing a crane and operator has a fixed cost regardless of how quick the pick is." },
      { q: "Is the operator included in the price?", a: "Always. We do not do bare-crane rentals — every quote includes a certified NCCCO operator." },
      { q: "How is mobilization calculated?", a: "It is based on round-trip travel from our DeLand yard to your jobsite. Local Volusia County work has minimal mob; longer runs to Jacksonville, Melbourne or the coast are quoted upfront." },
    ],
    relatedServices: ["crane-rental-operator", "hydraulic-mobile-crane"],
    relatedCities: ["orlando", "daytona-beach", "deland"],
    relatedArticles: ["what-size-crane-do-i-need", "crane-jobsite-preparation"],
  },

  "what-size-crane-do-i-need": {
    slug: "what-size-crane-do-i-need",
    title: "What Size Crane Do I Need? 40-Ton vs 50-Ton Explained",
    metaTitle: "What Size Crane Do I Need? 40-Ton vs 50-Ton Mobile Cranes",
    metaDescription:
      "Not sure whether you need a 40-ton or 50-ton mobile crane? Learn how capacity, reach and radius really work so you book the right crane for HVAC, steel, truss and machinery jobs in Central Florida.",
    keywords:
      "what size crane do i need, 40 ton vs 50 ton crane, crane capacity vs reach, how to pick a mobile crane, crane load chart basics",
    category: "Planning",
    readTime: "7 min read",
    image: GROVE,
    excerpt:
      "Capacity is only half the story. Here's how reach and radius decide which crane your job actually needs.",
    updated: "2025",
    body: [
      { type: "p", text: "The single biggest planning mistake we see is assuming a crane's rated capacity is what it can lift on your job. A '50-ton' crane does not lift 50 tons at every distance — it lifts 50 tons close in, and much less as the load moves away from the machine. Understanding that is the key to booking the right crane the first time." },
      { type: "h2", text: "Capacity vs. reach vs. radius" },
      { type: "p", text: "Three numbers decide every lift: how heavy the load is (capacity), how high it needs to go (reach), and how far out from the crane it sits (radius). As radius increases, the crane's safe capacity drops — sometimes dramatically. That is why two identical loads can need two different cranes depending on where they land." },
      { type: "h2", text: "Our 40-ton Grove — the versatile daily driver" },
      { type: "ul", items: [
        "Ideal for residential truss lifts and additions",
        "Rooftop HVAC unit swaps on low- and mid-rise buildings",
        "Sign, pole and light equipment sets",
        "Tight residential lots and neighborhood streets",
      ] },
      { type: "h2", text: "Our 50-ton Manitex — more capacity, more reach" },
      { type: "ul", items: [
        "Steel erection and pre-engineered metal buildings",
        "Heavier machinery moves and equipment sets",
        "Longer-reach commercial HVAC and chiller placements (up to ~155 ft main boom)",
        "Picks that sit farther from the crane's setup point",
      ] },
      { type: "callout", text: "You don't have to figure this out alone. Give us the load weight, the height and how far the crane can set up from the pick — we'll tell you exactly which crane fits and why." },
      { type: "h2", text: "What to have ready when you call" },
      { type: "ul", items: [
        "Weight of the heaviest item to be lifted",
        "How high it needs to go",
        "How far the crane can park from the set-down point",
        "Site access — gates, overhead power lines, soft ground",
        "Date and rough timeline",
      ] },
    ],
    faqs: [
      { q: "Can a 50-ton crane always lift more than a 40-ton?", a: "At the same radius, yes. But a job's real requirement depends on how far out the load sits. Sometimes reach — not raw capacity — is the deciding factor, which is why we do a quick pre-lift review." },
      { q: "What if I'm not sure of my load weight?", a: "Bring us the equipment spec sheet or model number and we can usually find the weight. For unknown loads we plan conservatively." },
      { q: "Do you provide lift plans?", a: "Yes — for critical lifts or when your GC/engineer requires it, we provide a written, signed lift plan." },
    ],
    relatedServices: ["steel-erection", "machinery-moving", "hvac-placement"],
    relatedCities: ["orlando", "sanford"],
    relatedArticles: ["crane-rental-cost-central-florida", "hvac-rooftop-crane-lift-guide"],
  },

  "hvac-rooftop-crane-lift-guide": {
    slug: "hvac-rooftop-crane-lift-guide",
    title: "HVAC Rooftop Unit Crane Lift: What to Expect",
    metaTitle: "HVAC Rooftop Crane Lift Guide — RTU & Chiller Placement",
    metaDescription:
      "Planning a rooftop HVAC unit swap or new install? Here's how a crane lift for RTUs, air handlers and chillers actually goes — coordination, roof protection, timing and safety in Central Florida.",
    keywords:
      "hvac rooftop crane lift, rtu crane lift guide, ac unit crane placement, chiller lift, hvac crane rental orlando",
    category: "How-To",
    readTime: "6 min read",
    image: HVAC,
    excerpt:
      "From delivery coordination to roof protection — how a rooftop HVAC lift really goes, start to finish.",
    updated: "2025",
    body: [
      { type: "p", text: "Rooftop HVAC placement is one of the most common crane jobs in Florida — and one of the most weather- and schedule-sensitive. Whether you are swapping a failed condenser or staging a bank of new RTUs, knowing how the lift flows helps everyone stay on schedule." },
      { type: "h2", text: "Before the lift: coordination" },
      { type: "p", text: "The smoothest HVAC lifts start with a short planning call. We coordinate with your mechanical contractor on unit weights, roof location, delivery timing and the set-down point on the curb. Getting the crane there when the delivery truck arrives eliminates dead time on your invoice." },
      { type: "h2", text: "On lift day" },
      { type: "ul", items: [
        "We arrive, set outriggers and pads, and run a pre-lift safety check",
        "The old unit (on a change-out) comes off the roof and onto the truck",
        "The new unit is rigged, lifted and set precisely on the curb",
        "Your HVAC crew handles the connections — we handle the steel in the air",
      ] },
      { type: "h2", text: "Protecting the building" },
      { type: "p", text: "Roof membranes, parapets and finished surfaces matter. We use skidboards, outrigger pads and crane mats where needed, and coordinate with the roofing contractor when a set is over new membrane." },
      { type: "callout", text: "Failed unit on a hot day? We run a 24/7 emergency line and same-day HVAC change-outs are often possible across Central Florida." },
      { type: "h2", text: "Weekend and after-hours lifts" },
      { type: "p", text: "Retail centers, restaurants and schools often need HVAC work done outside business hours to protect occupancy. Off-hours and weekend lifts are routine for us." },
    ],
    faqs: [
      { q: "How high can you reach for a rooftop unit?", a: "Our 50-ton Manitex reaches roughly 155 ft with the main boom — that covers virtually every rooftop RTU and chiller job in Central Florida." },
      { q: "Do you work directly with our HVAC subcontractor?", a: "Yes. Send us the equipment cut sheets and roof location and we coordinate schedule and rigging directly with your mechanical contractor." },
      { q: "Can you set a unit on a Saturday?", a: "Regularly. Weekend, evening and after-hours HVAC lifts are common to protect retail and school occupancy." },
    ],
    relatedServices: ["hvac-placement", "crane-rental-operator", "emergency-lifting"],
    relatedCities: ["orlando", "daytona-beach", "lake-mary"],
    relatedArticles: ["what-size-crane-do-i-need", "emergency-crane-services-storm"],
  },

  "crane-jobsite-preparation": {
    slug: "crane-jobsite-preparation",
    title: "How to Prepare Your Jobsite for a Crane",
    metaTitle: "How to Prepare Your Jobsite for a Crane — Contractor Checklist",
    metaDescription:
      "A simple pre-lift checklist to prep your Central Florida jobsite for a mobile crane: access, ground conditions, overhead hazards, staging and permits. Save time and money on lift day.",
    keywords:
      "crane jobsite preparation, crane setup requirements, ground bearing crane, prepare site for crane lift, crane access checklist",
    category: "How-To",
    readTime: "5 min read",
    image: SITE_HERO,
    excerpt:
      "A quick pre-lift checklist that keeps your crane day fast, safe and on budget.",
    updated: "2025",
    body: [
      { type: "p", text: "A crane that shows up to a site that is not ready burns time — and time is money on an operated rental. A few minutes of prep makes lift day fast and safe. Here is the checklist we walk every client through." },
      { type: "h2", text: "Access" },
      { type: "ul", items: [
        "Clear a path wide enough for the crane and any delivery trucks",
        "Confirm gate widths and turning room",
        "Remove parked vehicles, dumpsters or materials from the setup zone",
      ] },
      { type: "h2", text: "Ground conditions" },
      { type: "p", text: "Cranes exert heavy point loads through their outriggers. Soft ground, fresh fill, septic fields and buried utilities all matter. Let us know the ground conditions ahead of time so we bring the right matting." },
      { type: "h2", text: "Overhead hazards" },
      { type: "ul", items: [
        "Identify power lines — these dictate setup position and safe clearances",
        "Note trees, wires, canopies and adjacent structures",
        "Flag any low obstructions over the load path",
      ] },
      { type: "h2", text: "Staging and materials" },
      { type: "p", text: "Have the load staged and accessible where the crane can reach it. For truss and steel jobs, coordinate stacking order so the pick sequence flows." },
      { type: "callout", text: "Not sure about your ground or access? We do pre-lift site reviews. A five-minute call before the job avoids surprises on lift day." },
      { type: "h2", text: "Permits and traffic" },
      { type: "p", text: "Most standard jobs need no special permits. For street setups, lane closures or tight urban sites, we help coordinate traffic control and any local requirements." },
    ],
    faqs: [
      { q: "Do you need to see the site before the lift?", a: "For straightforward picks, a phone description is usually enough. For tight, complex or critical lifts, a pre-lift site visit is worth it." },
      { q: "What if my ground is soft?", a: "Tell us in advance. We bring outrigger pads and crane mats to spread the load safely on soft or finished surfaces." },
      { q: "Who handles permits?", a: "For standard configurations, none are needed. When street or lane closures are required, we help coordinate them as part of the job." },
    ],
    relatedServices: ["crane-rental-operator", "steel-erection", "truss-lifts"],
    relatedCities: ["deland", "sanford", "orlando"],
    relatedArticles: ["what-size-crane-do-i-need", "crane-rental-cost-central-florida"],
  },

  "truss-setting-crane-guide": {
    slug: "truss-setting-crane-guide",
    title: "Truss Setting With a Crane: A Contractor's Guide",
    metaTitle: "Truss Setting With a Crane — Residential & Commercial Guide",
    metaDescription:
      "How crane-assisted truss setting works for framers in Central Florida. Sequencing, stacking, timing and how to keep your crew moving on residential and commercial truss jobs.",
    keywords:
      "truss setting crane, residential truss lift, crane truss installation, framing crane rental, truss crane orlando",
    category: "How-To",
    readTime: "5 min read",
    image: GROVE,
    excerpt:
      "How to sequence a truss set so your framing crew never stands around waiting on the crane.",
    updated: "2025",
    body: [
      { type: "p", text: "Truss setting is a pace game. When the crane keeps up with a good framing crew, a house package flies. When it doesn't, everyone waits. Here is how we run truss lifts so your crew stays productive." },
      { type: "h2", text: "It starts with the load" },
      { type: "p", text: "The fastest sets happen when trusses come off the delivery truck in the order they will be installed. We coordinate with the crew leader on layout and stacking direction before the first pick." },
      { type: "h2", text: "Picking off the truck" },
      { type: "p", text: "In most cases we pick directly off the delivery truck rather than unloading first. It is faster and safer for the trusses, and it keeps the sequence tight." },
      { type: "h2", text: "The right crane for tight lots" },
      { type: "ul", items: [
        "Our 40-ton Grove is compact enough for tight residential lots and neighborhood streets",
        "Highway-legal and quick to set — great for production builders bouncing between homes",
        "Handles standard wood, LVL, engineered and light-steel trusses",
      ] },
      { type: "callout", text: "Truss deliveries never quite land on the day you planned. We flex with your schedule and book same-week whenever we can." },
      { type: "h2", text: "Keeping it safe" },
      { type: "p", text: "Wind, spans and rigging all factor into a safe truss set. Our operators speak the same signal language as your framing crew, so drops are controlled and predictable." },
    ],
    faqs: [
      { q: "How long does a typical residential truss set take?", a: "Most single-family homes take a half day to a full day depending on complexity, layout and how the trusses are stacked on the truck." },
      { q: "Do we need to unload the delivery truck first?", a: "Usually no — we pick directly off the truck, which is faster and safer for the trusses." },
      { q: "Can you work in tight neighborhood lots?", a: "Yes — the 40-ton Grove is sized for tight residential lots and streets." },
    ],
    relatedServices: ["truss-lifts", "crane-rental-operator", "hydraulic-mobile-crane"],
    relatedCities: ["deland", "sanford", "palm-coast"],
    relatedArticles: ["crane-jobsite-preparation", "what-size-crane-do-i-need"],
  },

  "emergency-crane-services-storm": {
    slug: "emergency-crane-services-storm",
    title: "When to Call for Emergency Crane Services",
    metaTitle: "Emergency Crane Services in Central Florida — Storm & Rescue Lifts",
    metaDescription:
      "Tree on a house, storm damage, equipment failure or collapse? Learn when to call for 24/7 emergency crane service in Central Florida and how the response works — insurance, timing and safety.",
    keywords:
      "emergency crane service, storm crane response, tree on house crane, 24 hour crane rental, hurricane recovery crane central florida",
    category: "Emergency",
    readTime: "5 min read",
    image: MANITEX,
    excerpt:
      "Storm damage or a failed lift can't wait. Here's when — and how — to call for a 24/7 emergency crane.",
    updated: "2025",
    body: [
      { type: "p", text: "Central Florida is hurricane country, and emergencies do not keep business hours. When something has fallen, failed or collapsed, a fast, safe crane response protects property and people. Here is when to call and what to expect." },
      { type: "h2", text: "When to call for an emergency lift" },
      { type: "ul", items: [
        "Tree on a house, vehicle or structure",
        "Post-storm roof or wall stabilization",
        "Failed HVAC or generator that needs an immediate swap",
        "Vehicle, boat or equipment recovery",
        "Broken or fallen sign, pole, antenna or industrial equipment",
      ] },
      { type: "h2", text: "How our emergency response works" },
      { type: "p", text: "Our emergency line rings a real dispatcher — not a call center. We assess the scope, quote the mobilization and get moving. From DeLand we can typically reach Orlando, Daytona, Sanford and the surrounding area within 60–90 minutes." },
      { type: "callout", text: "Emergency line: (386) 506-7771 — answered 24/7 for storm, accident and structural lifts across Central Florida." },
      { type: "h2", text: "Insurance and documentation" },
      { type: "p", text: "For insurance-covered work we can document the scene, provide itemized invoices and coordinate directly with your adjuster or restoration contractor." },
      { type: "h2", text: "Before the storm season" },
      { type: "p", text: "The best emergency plan is a phone number saved before you need it. Program our line now so that when a tree comes through the roof, the call is already easy." },
    ],
    faqs: [
      { q: "How fast can you respond to an emergency?", a: "From our DeLand yard we can typically be on-site in Orlando, Daytona, Sanford or the surrounding area within 60–90 minutes for true emergencies." },
      { q: "Do you work directly with insurance?", a: "Yes. For covered claims we document the scene, provide itemized invoices and communicate with adjusters and restoration contractors." },
      { q: "Are you available during hurricanes?", a: "We prioritize life-safety and structural stabilization first, then work insured-property calls in order. Save our number before the storm — do not wait for landfall." },
    ],
    relatedServices: ["emergency-lifting", "machinery-moving", "hvac-placement"],
    relatedCities: ["daytona-beach", "ormond-beach", "palm-coast"],
    relatedArticles: ["hvac-rooftop-crane-lift-guide", "crane-jobsite-preparation"],
  },
};

// Ordered list for pillar page, nav, sitemap
export const ARTICLE_SLUGS = [
  "crane-rental-cost-central-florida",
  "what-size-crane-do-i-need",
  "hvac-rooftop-crane-lift-guide",
  "crane-jobsite-preparation",
  "truss-setting-crane-guide",
  "emergency-crane-services-storm",
];
